<template>
    <MarkerContainer
        :showWarning="showWarning"
        :busy="isBusy"
        :fromOtherRoster="isOtherRoster"
        :toOtherRoster="isShiftOfOtherRoster"
        :readonly="!editable && !acceptable"
        :availabilityConflicted="hasWishConflict || hasAvailabilityConflict"
        :hasComment="hasComment"
        :isNormalState="isNormalState"
        :isSick="isSick"
        :markingColorId="shift?.template!.colorId"
        :isProposed="deploymentProposal !== undefined"

        @click:marker="changeDeployment"
        @contextmenu.prevent.stop="startContextMenu"
        @touchstart.passive.stop="startTouch"
        @touchmove.passive.stop="mobileContextMenu.moveTouch"
        @touchend.passive.stop="mobileContextMenu.endTouch"
        @drop.stop="handleDrop"
        @dragover.prevent.stop="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        :draggable="deploymentProposal === undefined"
        @dragstart="handleDragStart"
        @dragend="handleDragEnd"
    >
        {{ shiftLabel }}
        <DeploymentMarkerRosterName v-if="isShiftOfOtherRoster">
            ({{ rosterName }})
        </DeploymentMarkerRosterName>
        <div class="comment-container" v-if="publicComment">
            <i class="comment">{{ publicComment }}</i>
        </div>
        <div class="comment-container" v-if="specified">
            <i class="comment">Aangepast</i>
        </div>
    </MarkerContainer>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import ShiftStateUpdateModal from '@/components/modals/ShiftStateUpdateModal.vue';
    import {Deployment, Shift, Unavailability, PersonalPlanningWish, OrganisationDeploymentState, DeploymentProposal, DeploymentSpecification, DeploymentProposalSpecification, ApiConverter, DeploymentStateNumber} from '@cquential/api';
    import useMobileContextMenu from '@/util/useMobileContextMenu';
    import {RosterDay, ScreenCoordinates} from '@/types/types';
    import WishUtil from '@/util/WishUtil';
    import AvailabilityUtil from '@/util/AvailabilityUtil';
    import {DeploymentDragData, BasicDragData} from '@/types';
    import {ResourceWithKeyedByDateObjects, CompanyEmployeeWithDeployments, CompanyWithDeploymentsAndEmployees} from '@/types/api-object-extensions';
    import Planbord from '@/components/Planbord.vue';
    import {MobileContextMenu} from '@/components/types';
    import DeploymentUtil from '@/util/deploymentutil';
    import MarkerContainer from '@/components/Planbord/MarkerContainer.vue';
    import DeploymentMarkerRosterName from '@/components/Planbord/DeploymentMarkerRosterName.vue';
    import DeploymentProposalAcceptModal from '@/components/modals/DeploymentProposalAcceptModal.vue';
    import DeploymentCellContextMenu from '@/components/Planbord/DeploymentCellContextMenu.vue';

    @Component({
        components: {
            MarkerContainer,
            DeploymentMarkerRosterName,
        },
        emits: ['create:deployment', 'update:deployment', 'delete:deployment', 'create:deploymentProposal', 'update:deploymentProposal', 'delete:deploymentProposal'],
    })
    export default class DeploymentMarker extends Vue {
        @Prop() public shift?: Shift;
        @Prop() public deployment?: Deployment;
        @Prop() public deploymentProposal?: DeploymentProposal;
        @Prop({required: true}) public day!: RosterDay;
        @Prop() public resource?: ResourceWithKeyedByDateObjects;
        @Prop() public company?: CompanyWithDeploymentsAndEmployees;
        @Prop() public companyEmployee?: CompanyEmployeeWithDeployments;
        @Prop({type: Number, required: true}) public rosterId!: number;
        @Prop({type: Number, required: true}) public deploymentIndex!: number;
        @Prop({type: Boolean}) public showWarning!: boolean;
        @Prop({type: Boolean}) public isOtherRoster!: boolean;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({type: Boolean}) public editable!: boolean;
        @Prop({type: Boolean}) public acceptable!: boolean;
        @Prop() public wish?: PersonalPlanningWish;
        @Prop({required: true}) public unavailabilities!: Unavailability[];
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({type: Number, required: true}) public horizontalPosition!: number;
        @Prop({type: Number, required: true}) public verticalPosition!: number;

        private mobileContextMenu: MobileContextMenu = useMobileContextMenu();
        private deploymentMarker: HTMLElement | null = null;

        public get isBusy(): boolean {
            if (this.deployment) {
                return Boolean(!this.deployment.id || this.deployment.updating);
            }

            if (this.deploymentProposal) {
                return Boolean(!this.deploymentProposal.id || this.deploymentProposal.updating);
            }

            return false;
        }

        public get publicComment(): string | false {
            return this.deployment?.publicComment ?? this.deploymentProposal?.publicComment ?? false;
        }

        public get specified(): boolean {
            return this.deployment?.specified ?? this.deploymentProposal?.specified ?? false;
        }

        public startContextMenu(): void {
            if (this.mobileContextMenu.isTargetedDevice.value) {
                return;
            }
            this.openShiftStateUpdateModal();
        }

        public startTouch(event: TouchEvent): void {
            const promise: Promise<ScreenCoordinates> | null = this.mobileContextMenu.startTouch(event);
            if (promise) {
                promise.then(this.openShiftStateUpdateModal).catch((): void => {
                    return;
                });
            }
        }

        private openShiftStateUpdateModal(): void {
            this.closeContextMenus();
            let deployment: Deployment | false | undefined = undefined;
            let deploymentProposal: DeploymentProposal | false | undefined = undefined;

            if (!this.editable) {
                if (this.deploymentProposal && this.acceptable) {
                    /** @see DeploymentProposalAcceptModal.openModal() */
                    // @ts-expect-error TS2339
                    DeploymentProposalAcceptModal.__vfdConstructor.openModal(this.deploymentProposal, this.resource!);
                }

                return;
            }

            if (this.deployment) {
                deployment = 'id' in this.deployment ? this.deployment : false;
            }

            if (this.deploymentProposal) {
                deploymentProposal = 'id' in this.deploymentProposal ? this.deploymentProposal : false;
            }

            if (deployment === undefined && deploymentProposal === undefined) {
                throw Error('Trying to edit deployment of marker that\'s not displaying a deployment. It should either display a deployment or a deployment proposal.');
            }

            // typescript doesn't properly detect that "deployment ?? deploymentProposal" cannot be undefined, as in that case the above error is thrown => add ! to shut up the transpiler
            /** @see ShiftStateUpdateModal.openModal() */
            // @ts-expect-error TS2339
            ShiftStateUpdateModal.__vfdConstructor.openModal(this.resource, this.company, (deployment ?? deploymentProposal)!, new Date(this.day.date), deployment === undefined);
        }

        private closeContextMenus(): void {
            /** @see DeploymentCellContextMenu.hideMenu() */
            // @ts-expect-error TS2339
            DeploymentCellContextMenu.__vfdConstructor.hideMenu();
        }

        public handleDragOver(event: DragEvent): void {
            if (!(event.currentTarget instanceof HTMLElement)) {
                return;
            }

            event.currentTarget.style.opacity = '0.5';
            this.mobileContextMenu.moveTouch(event);
        }

        public handleDragLeave(event: DragEvent): void {
            if (!(event.currentTarget instanceof HTMLElement)) {
                return;
            }

            event.currentTarget.style.opacity = '';
            this.mobileContextMenu.moveTouch(event);
        }

        public handleDrop(event: DragEvent): void {
            if (!(event.currentTarget instanceof HTMLElement)) {
                return;
            }

            if (this.deploymentMarker && event.currentTarget.isSameNode(this.deploymentMarker)) {
                this.handleDragEnd(event);

                return;
            }

            if (!this.deployment) {
                // do not allow drop onto proposal marker (or invalid marker)
                return;
            }

            event.currentTarget.style.opacity = '1';
            if (event.dataTransfer) {
                let parsedData: DeploymentDragData;
                try {
                    parsedData = JSON.parse(event.dataTransfer.getData('text'));
                } catch {
                    event.preventDefault();

                    return;
                }
                this.prepareReceivedDataForAction(parsedData);
            }
        }

        public handleDragStart(event: DragEvent): void {
            if (!(event.currentTarget instanceof HTMLElement)) {
                return;
            }

            if (event.currentTarget) {
                this.deploymentMarker = event.currentTarget;
            }

            if (!this.deployment) {
                // do not allow drag of proposal
                return;
            }

            this.mobileContextMenu.registerPositionOnScreen(event);
            event.currentTarget.style.opacity = '0.5';
            this.mobileContextMenu.registerPositionOnScreen(event);
            this.prepareDataToTransferForAction(event, 'swap');
        }

        public handleDragEnd(event: DragEvent): void {
            if (!(event.currentTarget instanceof HTMLElement)) {
                return;
            }

            event.currentTarget.style.opacity = '';
            /** @see Planbord.setActiveCell() */
            // @ts-expect-error TS2339
            Planbord.__vfdConstructor.setActiveCell(this.horizontalPosition, this.verticalPosition, true);
            this.mobileContextMenu.endTouch(event);
            this.deploymentMarker = null;
        }

        private prepareReceivedDataForAction(data: DeploymentDragData): void {
            // Check if type is set and is 'swap'
            const droppedDeployment: Deployment = DeploymentUtil.convertDeploymentDragDataToRosterDeployment(data);
            if (data.type === 'swap') {
                // Fire function to swap dragged and dropped
                this.swapDeployments(droppedDeployment);

                return;
            }
            // Deployment = false (Rosterfree), Save directly
            if (!('id' in this.deployment!)) {
                // Create data for new deployment
                const deployment: Deployment = {
                    ...droppedDeployment,
                    date: this.day.date,
                    publicComment: null,
                    resourceId: droppedDeployment.resourceId ? droppedDeployment.resourceId : ((this.resource && this.resource.id) ? this.resource.id : null),
                };
                // Create deployment
                this.$emit('create:deployment', deployment);
            } else {
                // Set needed data for updating deployment
                const draggedData: Deployment = {
                    ...droppedDeployment,
                    id: this.deployment.id,
                    publicComment: this.deployment.publicComment,
                    resourceId: droppedDeployment.resourceId ? droppedDeployment.resourceId : ((this.resource && this.resource.id) ? this.resource.id : null),
                };
                // Set new shiftid which is needed to update in another function
                // Fire update function
                this.$emit('update:deployment', draggedData);
            }
        }

        private prepareDataToTransferForAction(event: DragEvent, type: string): void {
            // Define placeholder object
            let dataToTransfer: BasicDragData | DeploymentDragData;

            // Check if the dragged one is a rosterfree (e.g.: RV, -)
            if (!('id' in this.deployment!)) {
                // No deployment, set dataToTransfer to false
                dataToTransfer = {
                    type,
                    date: this.day.date.toISOString(),
                    resourceId: this.resource ? this.resource.id : undefined,
                    companyId: this.company ? this.company.id : undefined,
                    company: this.company,
                    companyEmployeeId: this.companyEmployee ? this.companyEmployee.id : undefined,
                    companyEmployee: this.companyEmployee,
                };
            } else {
                // Deployment, prepare placeholder for data to sent to the API
                dataToTransfer = {
                    type,
                    date: this.day.date.toISOString(),
                    shiftId: this.deployment.shiftId,
                    publicComment: this.deployment.publicComment,
                    stateId: this.deployment.stateId,
                    deploymentId: this.deployment.id,
                    resourceId: this.resource ? this.resource.id : undefined,
                    companyId: this.company ? this.company.id : undefined,
                    company: this.company,
                    companyEmployeeId: this.companyEmployee ? this.companyEmployee.id : undefined,
                    companyEmployee: this.companyEmployee,
                    specified: this.deployment.specified,
                    positionAssignmentPositionDeployments: this.deployment.positionAssignmentSetGroupPositionDeployments,
                    specifications: this.deployment.specifications?.map(ApiConverter.convertDeploymentSpecificationToRawSpecification),
                };
            }
            // Set data in datatransfer
            if (event.dataTransfer) {
                event.dataTransfer.setData('text', JSON.stringify(dataToTransfer));
            }
        }

        private swapDeployments(draggedDeployment: Deployment): void {
            if (!draggedDeployment.id && 'id' in this.deployment!) { // roster free dragged on this one
                draggedDeployment.id = this.deployment.id;
                draggedDeployment.publicComment = this.deployment.publicComment;
                draggedDeployment.shiftId = this.deployment.shiftId;
                draggedDeployment.stateId = this.deployment.stateId;
                draggedDeployment.specified = this.deployment.specified;
                draggedDeployment.specifications = this.deployment.specifications;
                this.$emit('create:deployment', draggedDeployment);
                this.$emit('delete:deployment', this.deployment);
            } else if ('id' in this.deployment!) { // swap of two deployments
                const deployment: Deployment = {
                    ...draggedDeployment,

                    publicComment: this.deployment.publicComment,
                    shiftId: this.deployment.shiftId,
                    stateId: this.deployment.stateId,
                    specified: this.deployment.specified,
                    specifications: this.deployment.specifications,
                };

                draggedDeployment.id = this.deployment.id;
                draggedDeployment.date = this.day.date;
                draggedDeployment.resourceId = this.resource?.id ?? null;
                draggedDeployment.companyId = this.company?.id ?? null;
                draggedDeployment.companyEmployeeId = this.companyEmployee?.id ?? null;

                this.$emit('update:deployment', draggedDeployment);
                this.$emit('update:deployment', deployment);
            } else { // this one is roster-free
                this.$emit('delete:deployment', draggedDeployment);
                const newDeployment: Deployment = {
                    ...draggedDeployment,
                    date: this.day.date,
                    resourceId: this.resource?.id ?? null,
                    companyId: this.company?.id ?? null,
                    companyEmployeeId: this.companyEmployee?.id ?? null,
                };
                this.$emit('create:deployment', newDeployment);
            }
        }

        public changeDeployment(): void {
            if (this.deploymentProposal !== undefined && this.acceptable) {
                return;
            }

            /** @see Planbord.setActiveCell() */
            // @ts-expect-error TS2339
            Planbord.__vfdConstructor.setActiveCell(this.horizontalPosition, this.verticalPosition, true, false, this.deploymentIndex);
        }

        public get isShiftOfOtherRoster(): boolean {
            return this.shift?.rosterId !== this.rosterId;
        }

        // fallbacks are bullshit, but there'll be plenty of errors if neither props are given
        public get stateId(): DeploymentStateNumber {
            return this.deployment?.stateId ?? this.deploymentProposal?.stateId ?? DeploymentStateNumber.Normal;
        }

        public get shiftId(): number {
            return this.deployment?.shiftId ?? this.deploymentProposal?.shiftId ?? -1;
        }

        public get specifications(): DeploymentSpecification[] | DeploymentProposalSpecification[] {
            return this.deployment?.specifications ?? this.deploymentProposal?.specifications ?? [];
        }

        public get isSick(): boolean {
            return this.stateId in this.organisationDeploymentStates && this.organisationDeploymentStates[this.stateId].properties.sick;
        }

        public get shiftLabel(): string {
            if (this.organisationDeploymentStates[this.stateId] && this.organisationDeploymentStates[this.stateId].code !== null) {
                return this.organisationDeploymentStates[this.stateId].code!;
            }

            return this.shift?.template!.code ?? '';
        }

        public get isNormalState(): boolean {
            return this.stateId === DeploymentStateNumber.Normal;
        }

        public get hasComment(): boolean {
            if (!this.deployment) {
                return false;
            }

            return !!this.deployment.publicComment;
        }

        public get hasWishConflict(): boolean {
            return (this.wish && WishUtil.hasWishConflicts(this.wish, this.shiftId, this.deploymentShiftStart, this.deploymentShiftEnd)) || false;
        }

        public get hasAvailabilityConflict(): boolean {
            if (!this.unavailabilities) {
                return false;
            }

            if (this.deployment && this.deployment.specifications) {
                return AvailabilityUtil.hasDeploymentConflict(
                    this.unavailabilities,
                    this.deployment.specifications.filter(
                        (specification: DeploymentSpecification): boolean => specification.stateId in this.organisationDeploymentStates && this.organisationDeploymentStates[specification.stateId].properties.working,
                    ),
                );
            }

            if (this.deploymentProposal && this.deploymentProposal.specified && this.deploymentProposal.specifications) {
                return AvailabilityUtil.hasDeploymentConflict(
                    this.unavailabilities,
                    this.deploymentProposal.specifications.filter(
                        (specification: DeploymentProposalSpecification): boolean => specification.stateId in this.organisationDeploymentStates && this.organisationDeploymentStates[specification.stateId].properties.working,
                    ),
                );
            }

            if (this.shift && this.day) {
                // stamp or proposal
                return AvailabilityUtil.hasShiftConflict(this.unavailabilities, this.day.date, this.shift);
            }

            return false;
        }

        public get deploymentShiftStart(): Date | undefined {
            return this.specifications!.length > 0 ? this.specifications![0].begin : undefined;
        }

        public get deploymentShiftEnd(): Date | undefined {
            if (this.specifications!.length === 0) {
                return undefined;
            }

            const specificationLength: number = this.specifications!.length - 1;

            return this.specifications![specificationLength].end;
        }

        public get rosterName(): string {
            if (this.shift) {
                return this.rosterNames[this.shift.rosterId] ?? 'Vervallen rooster';
            }

            return 'Vervallen rooster';
        }
    }
</script>

<style lang="scss" scoped>
    .comment-container {
        display: var(--comment-display, inline);

        + .comment-container::before {
            content: '';
            display: block;
        }
    }

    .comment {
        text-shadow: none;
        color: var(--deployment-marker-comment-color);
        border-radius: var(--theme-border-radius-small);
        background: var(--deployment-marker-comment-background);
        margin-right: 2px;
        hyphens: auto;
        word-break: normal;
        white-space: pre-wrap;
        margin-left: var(--cell-padding);
        padding: var(--comment-padding);
        font-weight: 700;
    }
</style>
